import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../context/LangContext';
import api from '../utils/api';
import './Products.css';

const EMPTY_PRODUCT = {
  type: 'product', name: '', price: '', currency: 'USD',
  description: '', ai_description: '', category: '', stock: 'unlimited', image_url: '', active: 1,
};

export default function Products() {
  const { lang } = useLang();
  const [tab, setTab] = useState('product');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_PRODUCT });
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get(`/rt/products?type=${tab}`);
      if (res.data?.success) setItems(res.data.data || []);
    } catch { /* ignore */ }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    fetchItems().finally(() => setLoading(false));
  }, [fetchItems]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_PRODUCT, type: tab });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      ...EMPTY_PRODUCT,
      ...item,
      price: item.price != null ? String(item.price) : '',
      active: item.active !== undefined ? item.active : 1,
    });
    setShowForm(true);
  };

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price === '' ? 0 : Number(form.price),
        active: form.active !== undefined ? form.active : 1,
      };
      if (editing) {
        await api.put(`/rt/products/${editing}`, payload);
      } else {
        await api.post('/rt/products', payload);
      }
      setShowForm(false);
      await fetchItems();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const msg = lang === 'en' ? 'Delete this item?' : '¿Eliminar este elemento?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/rt/products/${id}`);
      await fetchItems();
    } catch { /* ignore */ }
  };

  const isProduct = tab === 'product';

  return (
    <div className="products-page">
      <div className="products-header">
        <div>
          <h1>{lang === 'en' ? 'Products & Services' : 'Productos y Servicios'}</h1>
          <p>{lang === 'en' ? 'Add your catalog so the AI can answer customer questions accurately' : 'Agrega tu catálogo para que la IA responda preguntas de clientes con precisión'}</p>
        </div>
        <button type="button" className="prod-btn-add" onClick={openAdd}>
          + {lang === 'en' ? 'Add' : 'Agregar'} {isProduct ? (lang === 'en' ? 'Product' : 'Producto') : (lang === 'en' ? 'Service' : 'Servicio')}
        </button>
      </div>

      {/* Tabs */}
      <div className="products-tabs">
        <button type="button" className={`products-tab ${tab === 'product' ? 'products-tab--active' : ''}`} onClick={() => setTab('product')}>
          📦 {lang === 'en' ? 'Products' : 'Productos'}
        </button>
        <button type="button" className={`products-tab ${tab === 'service' ? 'products-tab--active' : ''}`} onClick={() => setTab('service')}>
          🛠️ {lang === 'en' ? 'Services' : 'Servicios'}
        </button>
      </div>

      {/* Loading */}
      {loading && <div className="prod-loading"><div className="spinner" /></div>}

      {/* Empty */}
      {!loading && items.length === 0 && (
        <div className="prod-empty">
          <div className="prod-empty__icon">{isProduct ? '📦' : '🛠️'}</div>
          <h3>{lang === 'en' ? `No ${isProduct ? 'products' : 'services'} yet` : `Aún no hay ${isProduct ? 'productos' : 'servicios'}`}</h3>
          <p>{lang === 'en' ? 'Add items so your AI can answer customer questions about pricing and availability' : 'Agrega elementos para que tu IA responda sobre precios y disponibilidad'}</p>
        </div>
      )}

      {/* Items Grid */}
      <div className="prod-grid">
        {items.map(item => (
          <div className="prod-card" key={item.id}>
            {item.image_url && (
              <div className="prod-card__img">
                <img src={item.image_url} alt={item.name} onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
            <div className="prod-card__body">
              <div className="prod-card__top">
                <h3>{item.name}</h3>
                {item.category && <span className="prod-card__cat">{item.category}</span>}
              </div>
              <div className="prod-card__price">
                {item.currency || 'USD'} {Number(item.price || 0).toFixed(2)}
                {item.type === 'service' && <span className="prod-card__price-note"> /{lang === 'en' ? 'session' : 'sesión'}</span>}
              </div>
              {item.description && <p className="prod-card__desc">{item.description}</p>}
              {item.ai_description && (
                <p className="prod-card__ai-desc">
                  <span className="prod-card__ai-badge">AI</span> {item.ai_description}
                </p>
              )}
              <div className="prod-card__meta">
                {item.stock && item.stock !== 'unlimited' && <span>📊 Stock: {item.stock}</span>}
                {item.stock === 'unlimited' && <span>♾️ {lang === 'en' ? 'Unlimited' : 'Ilimitado'}</span>}
              </div>
            </div>
            <div className="prod-card__actions">
              <button type="button" className="prod-btn-edit" onClick={() => openEdit(item)}>{lang === 'en' ? 'Edit' : 'Editar'}</button>
              <button type="button" className="prod-btn-del" onClick={() => handleDelete(item.id)}>{lang === 'en' ? 'Delete' : 'Eliminar'}</button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Form */}
      {showForm && (
        <div className="prod-modal-overlay" onClick={() => setShowForm(false)} role="presentation">
          <div className="prod-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="prod-modal-title">
            <h2 id="prod-modal-title">{editing ? (lang === 'en' ? 'Edit' : 'Editar') : (lang === 'en' ? 'Add' : 'Agregar')} {isProduct ? (lang === 'en' ? 'Product' : 'Producto') : (lang === 'en' ? 'Service' : 'Servicio')}</h2>

            <div className="prod-form">
              <div className="prod-form__row">
                <div className="prod-form__field">
                  <label htmlFor="prod-name">{lang === 'en' ? 'Name' : 'Nombre'} *</label>
                  <input id="prod-name" value={form.name} onChange={e => updateForm('name', e.target.value)}
                    placeholder={isProduct ? (lang === 'en' ? 'e.g. Blue Runner Sneakers' : 'ej. Zapatillas Blue Runner') : (lang === 'en' ? 'e.g. Gel Manicure' : 'ej. Manicure en Gel')} />
                </div>
                <div className="prod-form__field">
                  <label htmlFor="prod-category">{lang === 'en' ? 'Category' : 'Categoría'}</label>
                  <input id="prod-category" value={form.category} onChange={e => updateForm('category', e.target.value)}
                    placeholder={lang === 'en' ? 'e.g. Footwear, Nails' : 'ej. Calzado, Uñas'} />
                </div>
              </div>

              <div className="prod-form__row prod-form__row--3">
                <div className="prod-form__field">
                  <label htmlFor="prod-price">{lang === 'en' ? 'Price' : 'Precio'}</label>
                  <input id="prod-price" type="number" step="0.01" value={form.price} onChange={e => updateForm('price', e.target.value)} placeholder="0.00" />
                </div>
                <div className="prod-form__field">
                  <label htmlFor="prod-currency">{lang === 'en' ? 'Currency' : 'Moneda'}</label>
                  <select id="prod-currency" value={form.currency} onChange={e => updateForm('currency', e.target.value)}>
                    <option value="USD">USD</option><option value="EUR">EUR</option><option value="MXN">MXN</option>
                    <option value="COP">COP</option><option value="DOP">DOP</option><option value="BRL">BRL</option>
                  </select>
                </div>
                <div className="prod-form__field">
                  <label htmlFor="prod-stock">{isProduct ? 'Stock' : (lang === 'en' ? 'Duration' : 'Duración')}</label>
                  <input id="prod-stock" value={form.stock} onChange={e => updateForm('stock', e.target.value)}
                    placeholder={isProduct ? (lang === 'en' ? 'e.g. 50 or unlimited' : 'ej. 50 o unlimited') : (lang === 'en' ? 'e.g. 60 min' : 'ej. 60 min')} />
                </div>
              </div>

              <div className="prod-form__field">
                <label htmlFor="prod-desc">{lang === 'en' ? 'Description' : 'Descripción'}</label>
                <textarea id="prod-desc" rows={2} value={form.description} onChange={e => updateForm('description', e.target.value)}
                  placeholder={lang === 'en' ? 'General description for customers' : 'Descripción general para clientes'} />
              </div>

              <div className="prod-form__field">
                <label htmlFor="prod-ai-desc">
                  🤖 {lang === 'en' ? 'Description for AI' : 'Descripción para la IA'}
                  <span className="prod-form__hint"> — {lang === 'en' ? 'How should the AI present this to customers?' : '¿Cómo debería la IA presentar esto a los clientes?'}</span>
                </label>
                <textarea id="prod-ai-desc" rows={2} value={form.ai_description} onChange={e => updateForm('ai_description', e.target.value)}
                  placeholder={lang === 'en' ? 'e.g. Our best-selling sneaker, lightweight and breathable, great for running' : 'ej. Nuestro servicio más popular, incluye limado y esmaltado en gel de larga duración'} />
              </div>

              <div className="prod-form__field">
                <label htmlFor="prod-image">{lang === 'en' ? 'Image URL (optional)' : 'URL de Imagen (opcional)'}</label>
                <input id="prod-image" value={form.image_url} onChange={e => updateForm('image_url', e.target.value)}
                  placeholder="https://..." />
              </div>
            </div>

            <div className="prod-modal__actions">
              <button type="button" className="prod-btn-cancel" onClick={() => setShowForm(false)}>{lang === 'en' ? 'Cancel' : 'Cancelar'}</button>
              <button type="button" className="prod-btn-save" onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? '...' : (lang === 'en' ? 'Save' : 'Guardar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
